import React, { useRef } from 'react';

import { Button, Divider, Message, Segment } from 'semantic';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { request } from 'utils/api';
import {
  Search,
  Breadcrumbs,
  Layout,
  ListHeader,
  SearchFilters,
} from 'components';
import Table from './Table';
import { OnDataNeededFilterParams } from 'components/search/Provider';
import { FE_SYSTEM_FINANCE_PATH, BE_SYSTEM_INVOICES_PATH } from '../utils';
import { getInvoiceTypeOptions } from 'utils/invoicing';
import DropdownFilterV2 from '../../../components/search/Filters/DropdownV2';

const onDataNeeded = ({
  keywords,
  month,
  ...body
}: OnDataNeededFilterParams) => {
  const parsedMonth: { year: number; month: number } = {
    year: 0,
    month: 0,
  };
  if (month) {
    const result = month.split('-');
    parsedMonth.year = Number(result[0]);
    parsedMonth.month = Number(result[1]);
  }
  const isCsvFormat = body.format === 'csv';

  const suffix = isCsvFormat ? '' : '/fast';
  const path = `${BE_SYSTEM_INVOICES_PATH}/search${suffix}`;

  return request({
    method: 'POST',
    path,
    body: {
      ...body,
      ...parsedMonth,
      searchPhrase: keywords,
    },
  });
};

export default function Invoices() {
  const { t } = useTranslation();

  const searchRef = useRef<any>(null);

  const labels = {
    status: t('invoices.filterByStatus', 'Status'),
    type: t('invoices.filterByType', 'Type'),
    month: t('invoices.filterMonth', 'Month'),
    multipleExactReferences: t(
      'invoices.filterMultiExactReferences',
      'ERP Issues'
    ),
    excludeZero: t('invoices.filterExcludeZero', 'Exclude 0'),
    minAmount: t('invoices.filterMinAmount', 'Min Amount (vat excl.)'),
    providers: t('invoices.filterProviders', 'Filter By Providers'),
    netSuiteStates: t('invoices.filterNetSuiteStates', 'NetSuite States'),
  };

  return (
    <Search.Provider
      ref={searchRef}
      limit={100}
      filterMapping={{
        keywords: {
          name: 'keywords',
          type: 'string',
        },
        status: {
          type: 'string',
          name: 'status',
        },
        minAmount: {
          type: 'number',
          name: 'minAmount',
        },
        type: {
          type: 'string',
          name: 'type',
        },
        month: {
          type: 'string',
          name: 'month',
        },
        multipleExactReferences: {
          type: 'boolean',
          name: 'multipleExactReferences',
        },
        excludeZero: {
          type: 'boolean',
          name: 'excludeZero',
        },
        providers: {
          type: 'string',
          multiple: true,
          name: 'providers',
        },
        netSuiteStates: {
          type: 'string',
          multiple: true,
          name: 'netSuiteStates',
        },
      }}
      onDataNeeded={onDataNeeded}>
      <Breadcrumbs
        path={[
          <Link key="finance" to={FE_SYSTEM_FINANCE_PATH}>
            {t('finance.title', 'Finance')}
          </Link>,
        ]}
        active={t('invoices.title', 'Invoices')}
      />

      <ListHeader title={t('invoices.title', 'Invoices')}>
        <Search.Export filename="Invoices" />
      </ListHeader>

      <Segment>
        <Layout horizontal spread stackable>
          <SearchFilters.Modal size="tiny">
            <DropdownFilterV2
              name="status"
              label={labels.status}
              options={[
                {
                  key: 'error',
                  text: t('invoices.filterError', 'Error'),
                  value: 'error',
                },
                {
                  key: 'ready',
                  text: t('invoices.filterReady', 'Ready'),
                  value: 'ready',
                },
                {
                  key: 'pending',
                  text: t('invoices.filterPending', 'Pending'),
                  value: 'pending',
                },
                {
                  key: 'paid',
                  text: t('invoices.filterPaid', 'Paid'),
                  value: 'paid',
                },
              ]}
            />

            <SearchFilters.Dropdown
              label={labels.type}
              name="type"
              options={getInvoiceTypeOptions(t)}
            />

            <SearchFilters.Input
              label={labels.month}
              name="month"
              type="month"
              width={6}
            />

            <SearchFilters.Input
              label={labels.minAmount}
              name="minAmount"
              type="number"
            />

            <SearchFilters.Dropdown
              multiple
              label={labels.providers}
              name="providers"
              search
              onDataNeeded={(body) => {
                return request({
                  path: '/1/providers/search',
                  method: 'POST',
                  body,
                });
              }}
            />

            <Divider hidden />
            <Message
              info
              content={t(
                'invoices.netSuiteStatesInfo',
                'NetSuite States filter is only available for invoices that their provider has NetSuite as their ERP.'
              )}
            />
            <DropdownFilterV2
              multiple
              label={labels.netSuiteStates}
              name="netSuiteStates"
              options={[
                {
                  key: 'submitted',
                  text: t('invoices.netSuiteStateSubmitted', 'Submitted'),
                  value: 'submitted',
                },
                {
                  key: 'skipped',
                  text: t('invoices.netSuiteStateSkipped', 'Skipped'),
                  value: 'skipped',
                },
                {
                  key: 'error',
                  text: t('invoices.netSuiteStateError', 'Error'),
                  value: 'error',
                },
                {
                  key: 'queued',
                  text: t('invoices.netSuiteStateQueued', 'Queued'),
                  value: 'queued',
                },
                {
                  key: 'submissionAcknowledged',
                  text: t(
                    'invoices.netSuiteStateSubmissionAcknowledged',
                    'Submission Acknowledged'
                  ),
                  value: 'submissionAcknowledged',
                },
                {
                  key: 'unknown',
                  text: t('invoices.netSuiteStateUnknown', 'Unknown'),
                  value: 'unknown',
                },
              ]}
            />

            <Divider />
            <Layout horizontal>
              <SearchFilters.Checkbox
                label={labels.multipleExactReferences}
                name="multipleExactReferences"
              />
              <SearchFilters.Checkbox
                label={labels.excludeZero}
                name="excludeZero"
                style={{ marginLeft: '1em' }}
              />
            </Layout>
          </SearchFilters.Modal>

          <Layout horizontal stackable center right>
            <Search.Total />
            <SearchFilters.Search
              name="keywords"
              placeholder={t('systemInvoices.filterPlaceHolder', 'ID')}
            />
          </Layout>
        </Layout>
      </Segment>
      <Search.Status noResults={t('common.noResults', 'No Results')} />
      <Table />
      <div
        style={{
          textAlign: 'center',
        }}>
        <Search.Pagination />
      </div>
    </Search.Provider>
  );
}
